/* Reusable AI chat window + Action Center adapter. */
(function () {
  const { useCallback, useEffect, useMemo, useRef, useState } = React;

  function norm(value) { return String(value || "").trim().toLowerCase(); }
  function structuredQuestionDecision(value) {
    try {
      const parsed = JSON.parse(String(value || ""));
      return Array.isArray(parsed && parsed.answers);
    } catch {
      return false;
    }
  }
  function approvalToast(value) {
    const decision = norm(value);
    if (["accept", "allow", "approve", "approved"].includes(decision)) return "Approved";
    if (["abort", "cancel", "cancelled", "canceled"].includes(decision)) return "Cancelled";
    return "Declined";
  }
  function firstName(name) { return String(name || "Agent").trim().split(/\s+/)[0] || "Agent"; }
  function statusOf(run) { return norm(run && run.status) || "queued"; }
  function timeLabel(value) {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  function runStatusLabel(run) {
    const status = statusOf(run);
    if (status === "review") return "Ready for review";
    if (status === "needs-input") return "Waiting for you";
    if (["queued", "working", "waiting-delegates"].includes(status)) return "Working...";
    if (status === "error") return "Blocked";
    return "Online";
  }
  function runStatusTone(run) {
    const status = statusOf(run);
    if (status === "needs-input") return "waiting";
    if (status === "error") return "error";
    if (["queued", "working", "waiting-delegates", "review"].includes(status)) return "working";
    return "idle";
  }
  function userStopped(run) {
    return statusOf(run) === "error" && String(run && (run.error || run.Error) || "").trim().toLowerCase().startsWith("stopped by user");
  }
  function ChatAvatar({ employee, size = "" }) {
    const preset = window.avatarPreset ? window.avatarPreset(employee && employee.avatarKey) : null;
    const color = window.avatarColor ? window.avatarColor((employee && (employee.avatarKey || employee.name)) || "") : "#2856c4";
    if (preset && preset.image) {
      return <span className={`acw-agent-avatar ${size} has-image`}><img src={preset.image} alt="" aria-hidden="true" /></span>;
    }
    return (
      <span className={`acw-agent-avatar ${size}`} style={{ background: preset ? color : "linear-gradient(135deg, #2856c4, #6d4bd0)" }}>
        {preset ? <span className="acw-avatar-glyph" aria-hidden="true">{preset.glyph}</span> : <Icon name="star" size={15} stroke={1.7} />}
      </span>
    );
  }
  function sourceLabel(run) {
    return [run && (run.contextScopeLabel || run.projectName || run.customerName), run && run.threadId].filter(Boolean).join(" - ");
  }
  function detailsOf(change) { return (change && change.details) || (change && change.Details) || {}; }
  function detailValue(details, key) {
    if (!details) return undefined;
    const pascal = key.charAt(0).toUpperCase() + key.slice(1);
    return details[key] ?? details[pascal];
  }
  function messageKey(message, index) {
    return message.id || `${message.kind || "message"}-${index}`;
  }
  function messageMetadata(message) {
    const raw = message && (message.metadataJson || message.MetadataJson);
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  function messageRequestId(message) {
    const metadata = messageMetadata(message);
    return String(metadata.requestId || metadata.RequestId || "").trim();
  }
  function approvalFromRun(run) {
    const requestId = String(run && (run.approvalRequestId || run.ApprovalRequestId) || "").trim();
    const response = String(run && (run.approvalResponseText || run.ApprovalResponseText || run.approvalResponse || run.ApprovalResponse) || "").trim();
    if (!requestId || response) return null;
    return {
      requestId,
      method: run.approvalRequestMethod || run.ApprovalRequestMethod || "",
      title: run.approvalRequestTitle || run.ApprovalRequestTitle || "Approval required",
      body: run.approvalRequestBody || run.ApprovalRequestBody || "",
      payloadJson: run.approvalRequestPayloadJson || run.ApprovalRequestPayloadJson || "",
      response,
    };
  }
  function approvalPayload(approvalRequest) {
    const raw = approvalRequest && approvalRequest.payloadJson;
    if (!raw) return {};
    try {
      const payload = JSON.parse(raw);
      return payload && typeof payload === "object" ? payload : {};
    } catch {
      return {};
    }
  }
  function approvalRequestText(approvalRequest) {
    const title = String(approvalRequest && approvalRequest.title || "Needs your input").trim();
    const body = String(approvalRequest && approvalRequest.body || "").trim();
    return body && title ? `${title}\n\n${body}` : body || title;
  }
  function approvalJobIdForRun(run) {
    return norm(run && (run.sourceType || run.SourceType)) === "delegated-task" && (run.sourceId || run.SourceId)
      ? run.sourceId || run.SourceId
      : run && (run.id || run.Id);
  }
  function progressStatusClass(status) {
    const clean = norm(status);
    if (clean === "active" || clean === "pending") return clean;
    if (clean === "blocked") return "blocked";
    if (clean === "completed" || clean === "done") return "done";
    if (clean === "inprogress" || clean === "in_progress" || clean === "running") return "active";
    if (clean === "failed" || clean === "error" || clean === "errored") return "blocked";
    return "pending";
  }
  function planSteps(details) {
    const plan = detailValue(details, "plan");
    return Array.isArray(plan)
      ? plan.map((step) => ({
          step: step.step || step.Step || "",
          status: progressStatusClass(step.status || step.Status || "pending"),
        })).filter((step) => step.step)
      : [];
  }
  function numericDetail(details, key) {
    const value = detailValue(details, key);
    if (value === null || value === undefined || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }
  function tokenUsageFromDetails(details) {
    const usage = {
      inputTokens: numericDetail(details, "inputTokens"),
      outputTokens: numericDetail(details, "outputTokens"),
      totalTokens: numericDetail(details, "totalTokens"),
      cachedInputTokens: numericDetail(details, "cachedInputTokens"),
      contextWindowTokens: numericDetail(details, "contextWindowTokens"),
      contextUsedTokens: numericDetail(details, "contextUsedTokens"),
      contextRemainingTokens: numericDetail(details, "contextRemainingTokens"),
    };
    return Object.values(usage).some((value) => value !== null) ? usage : null;
  }
  function formatTokenCount(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return "";
    return Math.round(number).toLocaleString();
  }
  function tokenUsageLabel(usage) {
    if (!usage) return "";
    const used = usage.contextUsedTokens ?? usage.totalTokens;
    const windowTokens = usage.contextWindowTokens;
    if (used !== null && used !== undefined && windowTokens) return `${formatTokenCount(used)} / ${formatTokenCount(windowTokens)} tokens`;
    if (usage.totalTokens !== null && usage.totalTokens !== undefined) return `${formatTokenCount(usage.totalTokens)} tokens`;
    return "";
  }
  function tokenUsageTitle(usage) {
    if (!usage) return "";
    return [
      usage.inputTokens !== null && usage.inputTokens !== undefined ? `Input ${formatTokenCount(usage.inputTokens)}` : "",
      usage.outputTokens !== null && usage.outputTokens !== undefined ? `Output ${formatTokenCount(usage.outputTokens)}` : "",
      usage.cachedInputTokens !== null && usage.cachedInputTokens !== undefined ? `Cached ${formatTokenCount(usage.cachedInputTokens)}` : "",
      usage.contextRemainingTokens !== null && usage.contextRemainingTokens !== undefined ? `Remaining ${formatTokenCount(usage.contextRemainingTokens)}` : "",
    ].filter(Boolean).join(" - ");
  }
  function progressEvent(change) {
    const details = detailsOf(change);
    const changeKind = change.toolName || "";
    const method = detailValue(details, "eventMethod") || (norm(changeKind) === "assistant-delta" ? "item/agentMessage/delta" : "");
    const itemId = detailValue(details, "itemId") || "";
    const message = change.message || "";
    const delta = detailValue(details, "delta");
    const text = detailValue(details, "text");
    const messageType = detailValue(details, "messageType") || "";
    const textMode = detailValue(details, "textMode") || "";
    const eventId = `${method}:${itemId || change.version || change.changedAt || Date.now()}`;
    return {
      id: eventId,
      method,
      itemId,
      status: progressStatusClass(detailValue(details, "itemStatus") || change.toolName || ""),
      messageType,
      textMode,
      toolName: messageType || detailValue(details, "toolName") || "",
      command: detailValue(details, "command") || "",
      tokenUsage: tokenUsageFromDetails(details),
      delta: delta || (isLiveResponseMethod(method) ? (text || message) : ""),
      speechText: text || delta || (isLiveResponseMethod(method) ? message : ""),
      text: message || "Activity",
      createdAt: change.changedAt || new Date().toISOString(),
    };
  }
  function isLiveResponseMethod(method) {
    const clean = norm(method);
    return clean === "item/agentmessage/delta"
      || clean === "item/agent_message/delta"
      || clean === "item/reasoning/summarypartadded"
      || clean === "item/reasoning/summarytextdelta";
  }
  function isLiveResponseEvent(event) {
    const messageType = norm(event && event.messageType);
    const toolName = norm(event && event.toolName);
    const text = String((event && (event.speechText ?? event.delta ?? event.text)) || "").trim();
    return !!(event && text && (messageType === "assistant_response" || messageType === "assistant_thinking" || isLiveResponseMethod(event.method) || toolName === "assistant_response" || toolName === "assistant_thinking"));
  }
  function liveResponseText(event) {
    return isLiveResponseEvent(event) ? String((event.speechText ?? event.delta ?? event.text) || "").trim() : "";
  }
  function isAgentMessageDelta(event) {
    return isLiveResponseMethod(event && event.method);
  }
  function persistedAssistantTexts(messages) {
    return new Set((messages || [])
      .filter((message) => {
        const kind = norm(message && message.kind);
        const role = norm(message && message.role);
        return role === "assistant" || kind === "agent" || kind === "delegation-continuation" || kind === "done";
      })
      .map((message) => String(message && message.text || "").trim())
      .filter(Boolean));
  }
  function liveAssistantMessages(activity, messages) {
    const events = activity && Array.isArray(activity.events) ? activity.events : [];
    const persisted = persistedAssistantTexts(messages);
    return events
      .filter(isLiveResponseEvent)
      .map((event) => ({
        id: `live-assistant:${event.id}`,
        kind: "agent",
        role: "assistant",
        text: liveResponseText(event),
        createdAt: event.createdAt,
      }))
      .filter((message) => message.text && !persisted.has(message.text))
      .reverse();
  }
  function mergeProgressActivity(previous, change) {
    const details = detailsOf(change);
    const steps = planSteps(details);
    const event = progressEvent(change);
    const eventKey = event.itemId && event.method ? `${event.method}:${event.itemId}` : event.id;
    const previousEvents = (previous && previous.events) || [];
    const previousEvent = previousEvents.find((item) => {
      const key = item.itemId && item.method ? `${item.method}:${item.itemId}` : item.id;
      return key === eventKey;
    });
    const incomingSpeech = String(event.speechText || "").trim();
    const incomingDelta = String(event.delta || "");
    const hasAccumulatedSpeech = incomingSpeech && incomingSpeech.length > incomingDelta.length;
    const mergedDelta = previousEvent && isAgentMessageDelta(event)
      ? hasAccumulatedSpeech
        ? incomingSpeech
        : `${previousEvent.delta || previousEvent.speechText || ""}${incomingDelta || incomingSpeech}`
      : "";
    const mergedEvent = previousEvent && isAgentMessageDelta(event)
      ? { ...previousEvent, ...event, delta: mergedDelta, speechText: mergedDelta }
      : event;
    const existingEvents = previousEvents.filter((item) => {
      const key = item.itemId && item.method ? `${item.method}:${item.itemId}` : item.id;
      return key !== eventKey;
    });
    const tokenUsageUpdate = event.tokenUsage && norm(event.method) === "thread/tokenusage/updated";
    return {
      ...(previous || {}),
      id: `${change.jobId}:${change.version || change.changedAt || Date.now()}`,
      runId: change.jobId,
      text: tokenUsageUpdate ? ((previous && previous.text) || change.message || "Activity") : (change.message || (previous && previous.text) || "Activity"),
      createdAt: change.changedAt || new Date().toISOString(),
      plan: steps.length ? steps : ((previous && previous.plan) || []),
      tokenUsage: event.tokenUsage || (previous && previous.tokenUsage) || null,
      events: tokenUsageUpdate ? previousEvents.slice(0, 18) : [mergedEvent, ...existingEvents].slice(0, 18),
    };
  }

  function AiChatWindow({
    title,
    subtitle,
    statusTone = "idle",
    statusLabel = "Online",
    employee,
    run,
    messages,
    loading,
    error,
    busy,
    liveActivity,
    onClose,
    onRefresh,
    onArchiveToggle,
    onStopToggle,
    onDelete,
    onSend,
    approvalRequest,
    onRespondApproval,
    mentionOptions,
  }) {
    const [draft, setDraft] = useState("");
    const [openMeta, setOpenMeta] = useState({});
    const scrollRef = useRef(null);
    const stickToBottomRef = useRef(true);
    const textareaRef = useRef(null);
    const thread = messages || [];
    const liveMessages = useMemo(() => liveAssistantMessages(liveActivity, thread), [liveActivity, thread]);
    const pendingApproval = approvalRequest && approvalRequest.requestId && !approvalRequest.response ? approvalRequest : null;
    const needsInput = statusOf(run) === "needs-input";
    const stopped = userStopped(run);
    const review = statusOf(run) === "review";
    const canSend = !!draft.trim() && !busy && !loading && statusOf(run) !== "done";
    const pendingQuestionKey = useMemo(() => {
      if (!pendingApproval) return "";
      for (let index = thread.length - 1; index >= 0; index -= 1) {
        const message = thread[index];
        if (norm(message && message.kind) !== "question") continue;
        const requestId = messageRequestId(message);
        if (!requestId || requestId === pendingApproval.requestId) return messageKey(message, index);
      }
      return "";
    }, [thread, pendingApproval && pendingApproval.requestId]);

    useEffect(() => {
      stickToBottomRef.current = true;
    }, [run && run.id]);

    useEffect(() => {
      const node = scrollRef.current;
      if (!node || !stickToBottomRef.current) return;
      node.scrollTop = node.scrollHeight;
    }, [thread.length, liveMessages.length, loading, busy, liveActivity && liveActivity.id]);

    useEffect(() => {
      const onKeyDown = (event) => {
        if (event.key === "Escape") onClose && onClose();
      };
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    async function submit(text = draft) {
      const clean = String(text || "").trim();
      if (!clean || !onSend || busy) return;
      stickToBottomRef.current = true;
      await onSend(clean);
      setDraft("");
      if (textareaRef.current) textareaRef.current.style.height = "";
    }

    function onThreadScroll(event) {
      const node = event.currentTarget;
      stickToBottomRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80;
    }

    function onDraft(event) {
      setDraft(event.target.value);
      event.target.style.height = "auto";
      event.target.style.height = `${Math.min(130, event.target.scrollHeight)}px`;
    }

    function toggle(id) {
      setOpenMeta((current) => ({ ...current, [id]: !current[id] }));
    }

    return (
      <div className="acw-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && onClose) onClose(); }}>
        <section className="acw-panel" role="dialog" aria-modal="true" aria-label={title || "AI chat"}>
          <header className="acw-head">
            <ChatAvatar employee={employee} size="head" />
            <div className="acw-head-title">
              <strong>{title || "Assistant"}</strong>
              <span className="acw-status-row"><i className={`acw-status-dot ${statusTone}`} />{statusLabel}</span>
            </div>
            {onStopToggle && <button type="button" className={`acw-icon-btn ${stopped ? "" : "danger"}`} title={stopped ? "Resume thread" : "Stop thread"} onClick={onStopToggle} disabled={!!busy || !!loading}><Icon name={stopped ? "play" : "stop"} size={15} /></button>}
            {onArchiveToggle && <button type="button" className="acw-icon-btn" title={run && run.archived ? "Unarchive thread" : "Archive thread"} onClick={onArchiveToggle} disabled={!!busy || !!loading}><Icon name={run && run.archived ? "inbox" : "archive"} size={15} /></button>}
            {onDelete && <button type="button" className="acw-icon-btn danger" title="Delete thread" onClick={onDelete} disabled={!!busy || !!loading}><Icon name="trash" size={15} /></button>}
            {onRefresh && <button type="button" className="acw-icon-btn" title="Refresh thread" onClick={onRefresh} disabled={!!busy || !!loading}><Icon name="refresh" size={15} /></button>}
            <button type="button" className="acw-icon-btn" title="Close" onClick={onClose}><Icon name="close" size={15} /></button>
          </header>

          {subtitle && <div className="acw-context"><Icon name="handover" size={13} /><span>{subtitle}</span></div>}

          <div className="acw-thread" ref={scrollRef} onScroll={onThreadScroll}>
            <div className="acw-thread-inner">
              {loading && <div className="acw-empty">Loading thread...</div>}
              {!loading && error && <div className="acw-error"><Icon name="alert" size={13} />{error}</div>}
              {!loading && !error && thread.length === 0 && !liveActivity && <div className="acw-empty">Waiting for activity.</div>}
              {!loading && !error && thread.map((message, index) => {
                const key = messageKey(message, index);
                return (
                  <ChatMessage
                    key={key}
                    message={message}
                    employee={employee}
                    open={!!openMeta[message.id]}
                    onToggle={() => toggle(message.id)}
                    approvalRequest={pendingQuestionKey === key ? pendingApproval : null}
                    busy={busy}
                    onRespondApproval={onRespondApproval}
                  />
                );
              })}
              {!loading && !error && liveMessages.map((message, index) => (
                <ChatMessage
                  key={messageKey(message, index)}
                  message={message}
                  employee={employee}
                  open={false}
                  onToggle={() => {}}
                  approvalRequest={null}
                  busy={busy}
                  onRespondApproval={onRespondApproval}
                />
              ))}
              {!loading && !error && liveActivity && <LiveActivityBlock activity={liveActivity} />}
              {busy && <div className="acw-activity-line"><span className="acw-spinner" /><span>Updating thread...</span></div>}
            </div>
          </div>

          <footer className="acw-composer-wrap">
            {pendingApproval && !pendingQuestionKey && (
              <div className="acw-action-note approval">
                <span>{pendingApproval.title || "Approval required"}</span>
                <ApprovalActions approvalRequest={pendingApproval} busy={busy} onRespondApproval={onRespondApproval} />
              </div>
            )}
            {review && (
              <div className="acw-action-note review">
                <span>Ready for review. Send feedback to continue this run.</span>
              </div>
            )}
            {stopped && <div className="acw-error-note">This thread is stopped. Send a follow-up or press play to resume it.</div>}
            <div className={`acw-composer ${needsInput ? "needs" : ""}`}>
              <button type="button" className="acw-attach" title="Attachments are not available for this thread API" disabled><Icon name="paperclip" size={18} /></button>
              <MentionTextarea
                textareaRef={textareaRef}
                rows="1"
                value={draft}
                mentionOptions={mentionOptions}
                onValueChange={(nextValue, node) => {
                  setDraft(nextValue);
                  if (node && node.style) {
                    node.style.height = "auto";
                    node.style.height = `${Math.min(130, node.scrollHeight)}px`;
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    submit();
                  }
                }}
                placeholder={`${needsInput ? "Answer" : stopped ? "Follow up with" : "Message"} ${employee ? firstName(employee.name) : "the assistant"}...`}
                disabled={!!loading || statusOf(run) === "done"}
                data-appligator-id="action-center-chat-composer"
              />
              <button type="button" className="acw-send" disabled={!canSend} onClick={() => submit()} title="Send">
                <Icon name={busy ? "close" : "arrowUp"} size={17} />
              </button>
            </div>
            <div className="acw-toolbar">
              <button type="button" className="acw-model-chip" disabled title="Model selection is not exposed by this thread API"><Icon name="stack" size={13} />Default agent</button>
              <span>Enter send - Shift+Enter newline</span>
            </div>
          </footer>
        </section>
      </div>
    );
  }

  function ChatMessage({ message, employee, open, onToggle, approvalRequest, busy, onRespondApproval }) {
    const kind = norm(message.kind);
    const role = norm(message.role);
    if (kind === "step") {
      return (
        <div className="acw-activity-block">
          <button type="button" className="acw-thinking-summary" onClick={onToggle}>
            <Icon name="chevR" size={14} className={open ? "open" : ""} />
            <Icon name="star" size={13} />
            <span>{open ? "Activity" : message.text}</span>
          </button>
          {open && <div className="acw-thinking-body"><MarkdownText text={message.text} /></div>}
        </div>
      );
    }
    if (kind === "done") {
      return <div className="acw-done"><span><Icon name="check" size={13} /><MarkdownText text={message.text} /></span></div>;
    }
    if (kind === "error") {
      return <div className="acw-error"><Icon name="alert" size={13} /><MarkdownText text={message.text} /></div>;
    }
    if (kind === "question") {
      return (
        <div className="acw-question-wrap">
          <ApprovalRequestCard approvalRequest={approvalRequest} body={message.text} busy={busy} onRespondApproval={onRespondApproval} />
        </div>
      );
    }
    if (kind === "delegation-continuation") {
      return (
        <div className="acw-delegation-return">
          <div className="acw-delegation-return-label"><Icon name="handover" size={12} />Delegated work returned</div>
          <div className="acw-delegation-return-body"><MarkdownText text={message.text} /></div>
          {timeLabel(message.createdAt) && <time>{timeLabel(message.createdAt)}</time>}
        </div>
      );
    }
    if (kind === "you" || role === "user") {
      return (
        <div className="acw-user-message">
          <div className="acw-user-bubble"><MarkdownText text={message.text} /></div>
          {timeLabel(message.createdAt) && <time>{timeLabel(message.createdAt)}</time>}
        </div>
      );
    }
    return (
      <div className="acw-agent-message">
        <ChatAvatar employee={employee} />
        <div>
          <div className="acw-agent-bubble"><MarkdownText text={message.text} /></div>
          {timeLabel(message.createdAt) && <time>{timeLabel(message.createdAt)}</time>}
        </div>
      </div>
    );
  }

  function ApprovalRequestCard({ approvalRequest, body, busy, onRespondApproval, className = "" }) {
    const text = body == null ? approvalRequestText(approvalRequest) : String(body || "");
    return (
      <div className={`acw-question ${className}`}>
        <div className="acw-question-label"><Icon name="alert" size={12} />Needs your input</div>
        <div className="acw-question-body"><MarkdownText text={text} /></div>
        {approvalRequest && <ApprovalActions approvalRequest={approvalRequest} busy={busy} onRespondApproval={onRespondApproval} />}
      </div>
    );
  }

  function ApprovalActions({ approvalRequest, busy, onRespondApproval }) {
    if (!approvalRequest || !approvalRequest.requestId || !onRespondApproval) return null;
    const payload = approvalPayload(approvalRequest);
    const questions = Array.isArray(payload.questions)
      ? payload.questions.map((item, index) => ({
          id: String(item && item.id || `q${index + 1}`).trim(),
          question: String(item && item.question || "").trim(),
          answers: Array.isArray(item && item.answers) ? item.answers.map((answer) => String(answer || "").trim()).filter(Boolean) : [],
        })).filter((item) => item.id && item.question && item.answers.length)
      : [];
    if (String(payload.kind || "").toLowerCase() === "questions" && questions.length) {
      return <QuestionActions requestId={approvalRequest.requestId} questions={questions} busy={busy} onRespondApproval={onRespondApproval} />;
    }

    const respond = (decision) => onRespondApproval(approvalRequest.requestId, decision);
    const approveLabel = String(payload.approveLabel || (Array.isArray(payload.quickAnswers) && payload.quickAnswers[0]) || "Approve").trim();
    const declineLabel = String(payload.declineLabel || (Array.isArray(payload.quickAnswers) && payload.quickAnswers[1]) || "Decline").trim();
    return (
      <div className="acw-approval-actions">
        <button type="button" className="acw-approval-btn allow" disabled={!!busy} onClick={() => respond("approve")}>{approveLabel || "Approve"}</button>
        <button type="button" className="acw-approval-btn" disabled={!!busy} onClick={() => respond("decline")}>{declineLabel || "Decline"}</button>
      </div>
    );
  }

  function QuestionActions({ requestId, questions, busy, onRespondApproval }) {
    const [answers, setAnswers] = useState({});
    const complete = questions.every((question) => answers[question.id]);
    const respond = () => {
      if (!complete) return;
      onRespondApproval(requestId, JSON.stringify({
        answers: questions.map((question) => ({
          id: question.id,
          question: question.question,
          answer: answers[question.id],
        })),
      }));
    };
    return (
      <div className="acw-question-actions">
        {questions.map((question) => (
          <div className="acw-question-choice" key={question.id}>
            <div className="acw-question-choice-title">{question.question}</div>
            <div className="acw-question-choice-row">
              {question.answers.map((answer) => (
                <button
                  type="button"
                  key={answer}
                  className={answers[question.id] === answer ? "selected" : ""}
                  disabled={!!busy}
                  onClick={() => setAnswers((current) => ({ ...current, [question.id]: answer }))}
                >
                  {answer}
                </button>
              ))}
            </div>
          </div>
        ))}
        <button type="button" className="acw-approval-btn allow" disabled={!!busy || !complete} onClick={respond}>Submit answers</button>
      </div>
    );
  }

  function LiveActivityBlock({ activity }) {
    const [open, setOpen] = useState(false);
    const plan = activity && Array.isArray(activity.plan) ? activity.plan : [];
    const events = activity && Array.isArray(activity.events) ? activity.events : [];
    const tokenUsage = activity && activity.tokenUsage;
    const tokenLabel = tokenUsageLabel(tokenUsage);
    const active = events.some((event) => event.status === "active") || plan.some((step) => step.status === "active");
    const doneCount = events.filter((event) => event.status === "done").length;
    const title = active ? "Working..." : events.length ? `Live activity - ${doneCount}/${events.length} done` : "Live activity";

    return (
      <div className="acw-live-block">
        <div className="acw-live-box">
          <button type="button" className="acw-live-summary" onClick={() => setOpen((current) => !current)}>
            <Icon name="chevR" size={13} className={open ? "open" : ""} />
            <Icon name="tool" size={13} />
            <span>{title}</span>
            {tokenLabel && <em className="acw-token-usage" title={tokenUsageTitle(tokenUsage) || tokenLabel}>{tokenLabel}</em>}
          </button>
          {open && (
            <div className="acw-live-body">
              {activity.text && <div className="acw-live-note"><span className="acw-live-status active" /><strong>{activity.text}</strong><time>{timeLabel(activity.createdAt)}</time></div>}
              {tokenLabel && <TokenUsageRow usage={tokenUsage} />}
              {plan.map((step, index) => <LivePlanRow key={`${index}:${step.step}`} step={step} />)}
              {events.map((event) => <LiveEventRow key={event.id} event={event} />)}
            </div>
          )}
        </div>
      </div>
    );
  }

  function TokenUsageRow({ usage }) {
    const label = tokenUsageLabel(usage);
    if (!label) return null;
    const parts = [
      usage.inputTokens !== null && usage.inputTokens !== undefined ? `Input ${formatTokenCount(usage.inputTokens)}` : "",
      usage.outputTokens !== null && usage.outputTokens !== undefined ? `Output ${formatTokenCount(usage.outputTokens)}` : "",
      usage.cachedInputTokens !== null && usage.cachedInputTokens !== undefined ? `Cached ${formatTokenCount(usage.cachedInputTokens)}` : "",
      usage.contextRemainingTokens !== null && usage.contextRemainingTokens !== undefined ? `Remaining ${formatTokenCount(usage.contextRemainingTokens)}` : "",
    ].filter(Boolean);
    return (
      <div className="acw-live-token-row">
        <Icon name="stack" size={12} />
        <strong>{label}</strong>
        {parts.length > 0 && <em>{parts.join(" - ")}</em>}
      </div>
    );
  }

  function LivePlanRow({ step }) {
    const status = progressStatusClass(step && step.status);
    return (
      <div className={`acw-live-plan ${status}`}>
        <span>{status === "done" ? <Icon name="check" size={11} /> : null}</span>
        <p>{step.step}</p>
      </div>
    );
  }

  function LiveEventRow({ event }) {
    const status = progressStatusClass(event && event.status);
    const isStreamingResponse = isLiveResponseEvent(event);
    const streamText = liveResponseText(event);
    const detail = [event.toolName, event.command || (isStreamingResponse ? "" : event.delta)].filter(Boolean).join(" - ");
    return (
      <div className="acw-live-event">
        <span className={`acw-live-status ${status}`} />
        <div>
          <strong>{event.text || "Activity"}</strong>
          {detail && <em>{detail}</em>}
          {isStreamingResponse && <div className="acw-live-stream"><MarkdownText text={streamText} /></div>}
        </div>
        <time>{timeLabel(event.createdAt)}</time>
      </div>
    );
  }

  function ActionCenterRunChatModal({ employeeId, runId, overview, db, initialLiveActivity, onClose, onToast, onError, onRefreshOverview }) {
    const [state, setState] = useState({ loading: true, busy: false, error: "", run: null, messages: [], liveActivity: initialLiveActivity || null });
    const agents = overview && Array.isArray(overview.agents) ? overview.agents : [];
    const overviewRuns = overview && Array.isArray(overview.runs) ? overview.runs : [];
    const mentionOptions = useMemo(() => window.buildMentionOptions ? window.buildMentionOptions(db) : [], [db]);
    const employee = useMemo(() => {
      const runEmployeeId = state.run && (state.run.employeeId || state.run.agentId);
      return agents.find((agent) => String(agent.id) === String(runEmployeeId || employeeId)) || null;
    }, [agents, employeeId, state.run]);

    const loadRun = useCallback(async (silent = false) => {
      if (!runId || !window.API.getActionCenterRun) {
        setState((current) => ({ ...current, loading: false, error: "Action Center run endpoint is not available." }));
        return;
      }
      try {
        if (!silent) setState((current) => ({ ...current, loading: true, error: "" }));
        const update = await window.API.getActionCenterRun(runId);
        if (!update || update.exists === false) {
          setState((current) => ({ ...current, loading: false, error: "This Action Center run no longer exists.", run: null, messages: [] }));
          return;
        }
        setState((current) => ({ ...current, loading: false, error: "", run: update.run || current.run, messages: update.messages || [] }));
      } catch (error) {
        const message = (error && error.message) || "Failed to load thread.";
        setState((current) => ({ ...current, loading: false, error: message }));
        if (onError) onError(error);
      }
    }, [runId, onError]);

    useEffect(() => {
      setState((current) => ({ ...current, liveActivity: null }));
      loadRun(false);
    }, [loadRun]);
    useEffect(() => {
      if (!initialLiveActivity) return;
      setState((current) => ({ ...current, liveActivity: initialLiveActivity }));
    }, [initialLiveActivity && initialLiveActivity.id]);
    useEffect(() => {
      if (!runId) return undefined;
      if (!window.API.subscribeToChanges) {
        const id = setInterval(() => loadRun(true), 4500);
        return () => clearInterval(id);
      }
      let timer = null;
      const unsubscribe = window.API.subscribeToChanges((change) => {
        const jobId = change && change.jobId;
        const toolName = norm(change && change.toolName);
        const runMatches = String(jobId || "").toLowerCase() === String(runId || "").toLowerCase();
        if (!runMatches && !toolName.startsWith("action-center/")) return;
        if (runMatches && (toolName === "transient-activity" || toolName === "codex-progress")) {
          setState((current) => ({ ...current, liveActivity: mergeProgressActivity(current.liveActivity, change) }));
          return;
        }
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => loadRun(true), 120);
      });
      return () => {
        if (timer) clearTimeout(timer);
        unsubscribe();
      };
    }, [runId, loadRun]);
    useEffect(() => {
      if (!runId) return undefined;
      const status = statusOf(state.run);
      if (!["queued", "working", "needs-input", "waiting-delegates"].includes(status)) return undefined;
      const id = setInterval(() => loadRun(true), 4500);
      return () => clearInterval(id);
    }, [runId, state.run && state.run.status, loadRun]);

    async function withBusy(message, work) {
      try {
        setState((current) => ({ ...current, busy: true, error: "" }));
        await work();
        await loadRun(true);
        if (onRefreshOverview) await onRefreshOverview();
        if (onToast) onToast(message);
      } catch (error) {
        const text = (error && error.message) || "Request failed.";
        setState((current) => ({ ...current, error: text }));
        if (onError) onError(error);
      } finally {
        setState((current) => ({ ...current, busy: false }));
      }
    }

    async function deleteThread() {
      if (!runId || !window.API.deleteActionCenterRun) {
        setState((current) => ({ ...current, error: "Action Center delete endpoint is not available." }));
        return;
      }
      if (!window.confirm("Delete this thread? This removes it from the Action Center and queues Codex deletion when available.")) return;
      try {
        setState((current) => ({ ...current, busy: true, error: "" }));
        const result = await window.API.deleteActionCenterRun(runId);
        if (onRefreshOverview) await onRefreshOverview();
        if (onToast) {
          const codexQueued = result && result.codexDeleteQueued;
          const stopped = result && result.activePromptStopped;
          const codexUnavailable = result && (result.codexDeleteError || result.stopError);
          onToast(codexQueued ? "Thread deleted; Codex deletion queued" : stopped ? "Thread deleted; active prompt stopped" : codexUnavailable ? "Thread deleted locally; Codex stop/delete unavailable" : "Thread deleted");
        }
        if (onClose) onClose();
      } catch (error) {
        const text = (error && error.message) || "Failed to delete thread.";
        setState((current) => ({ ...current, error: text }));
        if (onError) onError(error);
      } finally {
        setState((current) => ({ ...current, busy: false }));
      }
    }

    async function toggleArchive() {
      const run = state.run || fallbackRun;
      if (!runId || !window.API.setActionCenterRunArchived) {
        setState((current) => ({ ...current, error: "Action Center archive endpoint is not available." }));
        return;
      }
      if (!run || !run.threadId) {
        setState((current) => ({ ...current, error: "This Action Center run does not have a persisted thread yet." }));
        return;
      }

      const nextArchived = !run.archived;
      await withBusy(nextArchived ? "Thread archived" : "Thread unarchived", () => window.API.setActionCenterRunArchived(runId, nextArchived));
    }

    const fallbackRun = overviewRuns.find((run) => String(run.id) === String(runId)) || null;
    const run = state.run || fallbackRun;
    const approvalRequest = approvalFromRun(run);
    const title = employee ? employee.name : "AI employee";
    const subtitle = [run && (run.title || "Untitled task"), sourceLabel(run)].filter(Boolean).join(" - ");
    async function respondApproval(requestId, decision) {
      const jobId = approvalJobIdForRun(run);
      if (!jobId || !requestId || !window.API.respondCodexPromptApproval) {
        setState((current) => ({ ...current, error: "Approval endpoint is not available." }));
        return;
      }
      const toast = structuredQuestionDecision(decision) ? "Answers submitted" : approvalToast(decision);
      await withBusy(toast, () => window.API.respondCodexPromptApproval(jobId, requestId, decision));
    }

    return (
      <AiChatWindow
        title={title}
        subtitle={subtitle}
        employee={employee}
        run={run}
        statusTone={runStatusTone(run)}
        statusLabel={runStatusLabel(run)}
        messages={state.messages}
        loading={state.loading}
        error={state.error}
        busy={state.busy}
        liveActivity={state.liveActivity}
        approvalRequest={approvalRequest}
        mentionOptions={mentionOptions}
        onClose={onClose}
        onRefresh={() => loadRun(false)}
        onArchiveToggle={run && run.threadId ? toggleArchive : null}
        onStopToggle={run && window.API.stopActionCenterRun && (userStopped(run) || ["queued", "working", "needs-input", "waiting-delegates"].includes(statusOf(run))) ? () => withBusy(userStopped(run) ? "Thread resumed" : "Thread stopped", () => window.API.stopActionCenterRun(runId)) : null}
        onDelete={deleteThread}
        onSend={(text) => withBusy(statusOf(run) === "needs-input" ? "Input sent" : "Message sent", () => window.API.sendActionCenterRunMessage(runId, text))}
        onRespondApproval={respondApproval}
      />
    );
  }

  Object.assign(window, {
    AiChatWindow,
    ActionCenterRunChatModal,
    ActionCenterRequestCard: ApprovalRequestCard,
    actionCenterApprovalFromRun: approvalFromRun,
    actionCenterApprovalJobIdForRun: approvalJobIdForRun,
    actionCenterApprovalToast: approvalToast,
    actionCenterStructuredQuestionDecision: structuredQuestionDecision,
    AiChatMergeProgressActivity: mergeProgressActivity,
  });
})();
